import React, { useState } from 'react';
import { Buffer } from 'buffer';
import api, { isLimitError } from '../api/api';
import { storage } from '../product/productStorage';
import { useCreateContext } from '../product/CreateContext';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { useCreateFlow, usePrefill } from './create/flow';
import './media.css';

const BAD_RESPONSE = "The image service sent back something we couldn't show. Please try again.";

// Image formats by their first bytes
const SIGNATURES = [
    { type: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47] },
    { type: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
    { type: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },
    { type: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46] },
];

const asBytes = (data) => {
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return null;
};

// A JSON body instead of bytes: accept { url } or { b64_json }, alone, in a list or under data
const imageFromJson = (bytes) => {
    try {
        const value = JSON.parse(new TextDecoder().decode(bytes));
        const item = Array.isArray(value) ? value[0] : Array.isArray(value?.data) ? value.data[0] : value;
        if (typeof item?.url === 'string' && /^(https?:|data:image\/)/.test(item.url)) return item.url;
        if (typeof item?.b64_json === 'string' && item.b64_json) return `data:image/png;base64,${item.b64_json}`;
    } catch (e) {
        // Not JSON
    }
    return null;
};

// The API answers with the image's bytes; turn them into something an <img> can show, or null
const toImageSrc = (data) => {
    const bytes = asBytes(data);
    if (!bytes || bytes.length === 0) return null;
    const match = SIGNATURES.find(signature => signature.bytes.every((byte, i) => bytes[i] === byte));
    if (match) return `data:${match.type};base64,${Buffer.from(bytes).toString('base64')}`;
    // '{' or '[': a JSON answer, never an image
    if (bytes[0] === 0x7b || bytes[0] === 0x5b) return imageFromJson(bytes);
    return `data:image/png;base64,${Buffer.from(bytes).toString('base64')}`;
};

const extensionOf = (src) => (src.match(/^data:image\/(\w+)/)?.[1] || 'png').replace('jpeg', 'jpg');

// A starting description from what the earlier steps made
const suggestedDescription = (kind, flow) => {
    if (kind === 'blog' && flow.article?.title) return `A cover image for a blog article titled "${flow.article.title}", clean editorial style, no text`;
    if (kind === 'ads' && (flow.ad?.headline || flow.ad?.primaryText)) return `An ad creative for: ${flow.ad.headline || flow.ad.primaryText}. Product in use, bold and simple, no text`;
    if (flow.hook) return `A scroll-stopping social post image for: ${flow.hook}. No text`;
    return '';
};

const ImageScreen = () => {
    const { embedded, seed } = useCreateContext();
    const { flow, report, kind } = useCreateFlow();
    // A planned post's topic beats the last description typed for another post
    const [imageDescription, setImageDescription] = useState(() => (seed?.topic && seed.topic) || storage.get('imageDescription') || '');
    usePrefill(suggestedDescription(kind, flow), imageDescription, setImageDescription);
    // The flow keeps the image while this step is mounted, so Review can show it
    const [generatedImage, setGeneratedImageState] = useState(() => flow.image || '');
    const setGeneratedImage = (src) => {
        setGeneratedImageState(src);
        report('image', { image: src, imagePrompt: src ? imageDescription : '' }, Boolean(src));
    };
    const [fieldError, setFieldError] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleGenerateImage = async () => {
        if (!imageDescription.trim()) {
            setFieldError('Describe the image you want to generate.');
            return;
        }

        setFieldError('');
        setError('');
        setIsLoading(true);
        const prompt = `Generate an image of ${imageDescription}`;

        try {
            const src = toImageSrc(await api.generateImage(prompt));
            if (!src) {
                setError(BAD_RESPONSE);
                return;
            }
            setGeneratedImage(src);
        } catch (err) {
            if (isLimitError(err)) return;
            setError("Couldn't generate the image. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Remember the description for this product
    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        setImageDescription(value);
        storage.set('imageDescription', value);
        if (value.trim()) setFieldError('');
    };

    // Bytes that looked fine but don't decode as an image
    const handleImageError = () => {
        setGeneratedImage('');
        setError(BAD_RESPONSE);
    };

    return (
        <div className="ls-col">
            {!embedded && (
                <div className="ls-page-header">
                    <h1 className="ls-page-title">Image generator</h1>
                </div>
            )}

            <div className="ls-two-col">
                <div className="ls-card">
                    <div className="ls-card-title">Image brief</div>
                    <div className="ls-form">
                        <label className="ls-field">
                            <span className="ls-label">Describe the image</span>
                            <textarea
                                className={`ls-textarea${fieldError ? ' ls-media-invalid' : ''}`}
                                rows={4}
                                value={imageDescription}
                                onChange={handleDescriptionChange}
                                placeholder="e.g. A nurse unpacking a colourful meal-prep box in a bright hospital break room, natural light"
                                aria-invalid={Boolean(fieldError)}
                                aria-describedby="ls-image-description-hint"
                            />
                            <span id="ls-image-description-hint" className={`ls-hint${fieldError ? ' ls-media-error' : ''}`} role={fieldError ? 'alert' : undefined}>
                                {fieldError || 'Name the subject, setting, style and lighting.'}
                            </span>
                        </label>
                        <button
                            type="button"
                            className={`ls-btn ${generatedImage ? 'ls-btn-secondary' : 'ls-btn-primary'}`}
                            onClick={handleGenerateImage}
                            disabled={isLoading}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            {isLoading ? <span className="ls-spinner" /> : <Icon name="auto_awesome" size={16} />}
                            {isLoading ? 'Generating...' : generatedImage ? 'Generate again' : 'Generate image'}
                        </button>
                    </div>
                </div>

                <div className="ls-card">
                    <div className="ls-card-header">
                        <span className="ls-card-title">Image</span>
                        {generatedImage && !isLoading ? (
                            <a className="ls-btn ls-btn-pill" href={generatedImage} download={`image.${extensionOf(generatedImage)}`}>
                                <Icon name="download" size={14} />Download
                            </a>
                        ) : (
                            <button type="button" className="ls-btn ls-btn-pill" disabled>
                                <Icon name="download" size={14} />Download
                            </button>
                        )}
                    </div>
                    {error && <Alert severity="error">{error}</Alert>}
                    <div className="ls-media" aria-busy={isLoading}>
                        {isLoading ? (
                            <>
                                <div className="ls-skeleton ls-media-fill" />
                                <div className="ls-media-status" role="status">
                                    <div className="ls-media-status-row">
                                        <span className="ls-spinner" />Generating your image...
                                    </div>
                                </div>
                            </>
                        ) : generatedImage ? (
                            <img src={generatedImage} alt={imageDescription || 'Generated image'} onError={handleImageError} />
                        ) : (
                            <div className="ls-placeholder ls-media-fill">
                                <Icon name="image" />
                                Your image will appear here
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageScreen;
